import type { CrmSession } from "./types";

const APP = "Aurélia CRM";
const TILE = "CRM";

let verified = false;

function suiteBase(): string {
  if (window.__aureliaOwnSite) return "";
  const raw = window.location.pathname || "/";
  const idx = raw.toLowerCase().indexOf("/crm");
  const path = (idx >= 0 ? raw.slice(0, idx) : raw).replace(/\/+$/u, "");
  return path === "/" ? "" : path;
}

function isEmbedded(): boolean {
  try {
    return window.parent !== window;
  } catch {
    return true;
  }
}

function readUserId(): string {
  if (window.__aureliaOwnSite) {
    const hubId = String(window.__aureliaHub2UserId || "");
    return /^\d+$/.test(hubId) ? hubId : "";
  }
  const shared = window.readStoredSuiteUserId?.();
  if (shared != null && /^\d+$/.test(String(shared))) return String(shared);
  try {
    const stored = localStorage.getItem("userId") || localStorage.getItem("suite_user_id");
    if (stored && /^\d+$/.test(stored)) return stored;
  } catch {
    /* ignore */
  }
  return "";
}

function splash(text: string, hideBar = false): void {
  const el = document.getElementById("crmSplashText");
  if (el) el.textContent = text;
  if (hideBar) {
    const track = document.querySelector("#crmSplash .track");
    if (track instanceof HTMLElement) track.style.display = "none";
  }
}

function hideSplash(immediate = false): void {
  const el = document.getElementById("crmSplash");
  if (!el || el.dataset.leaving === "1") return;
  el.dataset.leaving = "1";
  const born = window.__crmBootBorn || Date.now();
  const wait = immediate ? 0 : Math.max(0, 1900 - (Date.now() - born));
  window.setTimeout(() => {
    el.classList.add("crm-boot--exiting");
    window.setTimeout(() => el.remove(), 420);
  }, wait);
}

async function fetchJson(
  url: string,
  credentials: RequestCredentials,
  headers?: HeadersInit,
): Promise<{ ok: boolean; status: number; data: Record<string, unknown> }> {
  const res = await fetch(url, { method: "GET", credentials, cache: "no-store", headers });
  let data: Record<string, unknown> = {};
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    data = {};
  }
  return { ok: res.ok, status: res.status, data };
}

function showNeedLogin(sessionLost: boolean): void {
  window.showSuiteAuthModal?.(undefined, { autoReconnect: !sessionLost });
  hideSplash(true);
}

async function verify(userId: string): Promise<CrmSession> {
  const sso = await fetchJson(
    `${suiteBase()}/api_suite/sso-verify.php?suite_user_id=${encodeURIComponent(userId)}&app=${encodeURIComponent(TILE)}`,
    "include",
  );
  if (!sso.ok || sso.data.success !== true) {
    verified = false;
    const err = String(sso.data.error || "");
    if (err === "app_not_permitted" || err === "forbidden_app_permission" || err === "admin_apps_disabled" || sso.status === 403) {
      window.showSuiteForbiddenModal?.({ appName: APP });
      splash("Cette tuile n’est pas autorisée pour ce compte.", true);
      return { ok: false, reason: "forbidden" };
    }
    showNeedLogin(true);
    return { ok: false, reason: "auth" };
  }

  const ping = await fetchJson(`${suiteBase()}/crm/api/ping.php`, "include", { "X-User-Id": userId });
  if (!ping.ok || ping.data.success === false) {
    verified = false;
    if (ping.data.error === "no_active_client") {
      splash("Accès non disponible. Rattache ce compte à un groupe CRM dans Pilotage.", true);
      return { ok: false, reason: "no_group" };
    }
    if (ping.status === 401) {
      showNeedLogin(true);
      return { ok: false, reason: "auth" };
    }
    splash("Le CRM ne répond pas.", true);
    return { ok: false, reason: "auth" };
  }

  verified = true;
  window.hideSuiteAuthModal?.();
  window.hideSuiteForbiddenModal?.();
  window.startSuiteHubSessionWatch?.();
  const client = ping.data.client as { id?: number; name?: string } | undefined;
  hideSplash();
  return {
    ok: true,
    userId,
    client: { id: Number(client?.id || 0), name: String(client?.name || "Groupe") },
  };
}

function loseSession(): void {
  verified = false;
  window.stopSuiteIframeSsoPoll?.();
  window.stopSuiteHubSessionWatch?.();
  if (!isEmbedded()) window.clearSuiteStoredIdentity?.();
  showNeedLogin(true);
}

export function bootCrmAuth(): Promise<CrmSession> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (session: CrmSession) => {
      if (settled || !session.ok) return;
      settled = true;
      resolve(session);
    };

    const start = () => {
      window.suiteAuthModalMount?.({ appName: APP });
      window.suiteForbiddenModalMount?.({ appName: APP });
      if (typeof window.installSuiteAuthPopup !== "function") {
        splash("Kit de connexion indisponible.", true);
        resolve({ ok: false, reason: "auth" });
        return;
      }
      window.installSuiteClearCacheListener?.({
        preserveKeys: ["userId", "suite_user_id", "userName", "email", "crm_theme", "crm_accent", "crm-space", "crm-sidebar-slim"],
      });
      window.installSuiteAuthPopup({
        redirect: "crm",
        isConnected: () => verified,
        onAuthenticated: (userId) => {
          const id = String(userId || readUserId());
          if (id && !window.__aureliaOwnSite) window.persistSuiteUserId?.(id);
          if (!id) return;
          void verify(id).then(finish);
        },
        onSessionLost: () => {
          loseSession();
        },
        patchFetch: false,
      });
      if (!window.__aureliaOwnSite) window.installSuiteHubSessionWatch?.({
        whoamiUrl: `${suiteBase()}/api_suite/session/whoami.php`,
        isEmbeddedInHub: isEmbedded,
        hasStoredIdentity: () => Boolean(readUserId()),
        isAppSessionActive: () => verified,
        onSessionLost: () => {
          loseSession();
        },
      });

      window.setTimeout(() => {
        if (window.__aureliaOwnSite) {
          void window.aureliaHub2User?.().then((id) => {
            if (!id) {
              showNeedLogin(false);
              return;
            }
            window.__aureliaHub2UserId = String(id);
            void verify(id).then(finish);
          });
          return;
        }
        const userId = readUserId();
        if (!userId) {
          showNeedLogin(false);
          return;
        }
        void verify(userId).then(finish);
      }, isEmbedded() ? 400 : 0);
    };

    if (window.__SUITE_SHARED_READY__) start();
    else document.addEventListener("suiteSharedReady", start, { once: true });
  });
}

export function suiteRoot(): string {
  return suiteBase();
}

export function apiUrl(file: string): string {
  return `${suiteBase()}/crm/api/${file}`;
}

export function currentUserId(): string {
  return readUserId();
}

export function currentUserQuery(): string {
  const id = readUserId();
  return id ? `?suite_user_id=${encodeURIComponent(id)}` : "";
}
